import React, { useEffect, useRef, useState } from 'react';
import { Play, Volume2, VolumeX, Maximize2 } from 'lucide-react';
import Watermark from '../common/Watermark';

const videoRegistry = new Set();

function pauseOthers(current) {
  videoRegistry.forEach((v) => {
    if (v !== current && !v.paused) v.pause();
  });
}

function ReelCard({ reel }) {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true); // default muted (autoplay-friendly)

  // Aauta reel ma aawaz kholepachhi aru reels auto-mute
  const toggleMute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    setMuted(next);
    if (!next) {
      videoRegistry.forEach((other) => {
        if (other !== v) {
          other.muted = true;
        }
      });
    }
  };

  const toggleFullscreen = (e) => {
    e.stopPropagation();
    const el = wrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (el.requestFullscreen) {
      el.requestFullscreen();
    }
  };

  useEffect(() => {
    const v = videoRef.current;
    if (v) videoRegistry.add(v);
    return () => {
      if (v) videoRegistry.delete(v);
    };
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused || v.ended) {
      pauseOthers(v);
      v.play();
    } else {
      v.pause();
    }
  };

  return (
    <div
      ref={wrapRef}
      className="reel-card group relative flex-[0_0_160px] md:flex-[0_0_190px] aspect-[9/16] rounded-2xl overflow-hidden snap-start bg-ink cursor-pointer"
      onClick={togglePlay}
    >
      <video
        ref={videoRef}
        className="w-full h-full object-cover block"
        src={reel.video || 'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4'}
        poster={reel.poster || undefined}
        muted
        loop
        playsInline
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />

      {/* Auto-rotating watermark — click garda zoom, harek 3 min ma position change */}
      <Watermark size={24} opacity={0.45} />

      {/* Play / pause indicator */}
      <span
        className={`absolute inset-0 m-auto w-11 h-11 rounded-full bg-black/55 text-white flex items-center justify-center backdrop-blur-sm pointer-events-none transition-opacity duration-200 ${
          playing ? 'opacity-0 group-hover:opacity-90' : 'opacity-100'
        }`}
      >
        <Play size={20} className="ml-0.5" />
      </span>

      {/* Volume + Fullscreen controls */}
      <div
        className="absolute right-2 bottom-2 flex flex-col gap-1.5 z-10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? 'आवाज खोल्नुहोस्' : 'आवाज बन्द गर्नुहोस्'}
          title={muted ? 'आवाज खोल्नुहोस्' : 'आवाज बन्द'}
          className={`w-8 h-8 rounded-full bg-black/55 backdrop-blur-sm text-white flex items-center justify-center transition-all duration-200 hover:bg-crimson hover:scale-110 ${
            muted ? 'opacity-70 group-hover:opacity-100' : 'opacity-100 ring-1 ring-white/70'
          }`}
        >
          {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
        </button>
        <button
          type="button"
          onClick={toggleFullscreen}
          aria-label="फुल स्क्रिन"
          title="फुल स्क्रिन"
          className="w-8 h-8 rounded-full bg-black/55 backdrop-blur-sm text-white flex items-center justify-center opacity-70 group-hover:opacity-100 transition-all duration-200 hover:bg-crimson hover:scale-110"
        >
          <Maximize2 size={13} />
        </button>
      </div>

      {reel.title && (
        <div className="absolute left-0 right-10 bottom-0 px-3 pt-6 pb-3 bg-gradient-to-t from-black/75 to-transparent text-white text-[12.5px] font-semibold leading-snug pointer-events-none">
          {reel.title}
        </div>
      )}
    </div>
  );
}

export default ReelCard;